/**
 * VariantsBulkCreateForm — wizard 5 étapes pour décliner une œuvre en
 * N variantes (refonte POD v6.1, PR-C).
 *
 *  Étape 1 — Type d'édition : applied à toutes les variantes du batch
 *           (Original / Édition limitée / Open / Set numérique).
 *  Étape 2 — Formats à décliner : multi-select filtré par compatibilité
 *           ratios sources + medium_kind. Chaque format coché = 1 variante.
 *  Étape 3 — Composition par défaut : support / encadrement / spec POD.
 *           1 valeur appliquée à toutes (override par ligne en étape 4).
 *           Skippée si edition = digital.
 *  Étape 4 — Prix & stock par ligne : table éditable N rows. Prefill prix
 *           via atelier pricing si dispo.
 *  Étape 5 — Récap + Submit : `useBulkAddVariants` → BE valide chaque
 *           payload AVANT d'ouvrir la transaction (tout-ou-rien). Une
 *           ligne invalide rollback toute la batch — on surface l'erreur
 *           globale + reset à l'étape qui pose problème.
 *
 * Pattern aligné sur `<PrintableSpecModal>` (wizard 3 étapes refonte v6.1).
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Circle, CircleDot, Disc } from 'lucide-react'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import Modal from '@/components/ui/Modal'
import RadioCard from '@/components/ui/RadioCard'
import Select from '@/components/ui/Select'
import { useFormats, useFramings, useSupports } from '@/hooks/useCatalog'
import { useBulkAddVariants } from '@/hooks/useWorks'
import type { FormatResponse, FramingResponse, SupportResponse } from '@/types/catalog'
import {
  EDITION_KIND_FR,
  type WorkVariantInput,
  type WorkVariantWithCatalogResponse,
} from '@/types/works'

import './VariantsBulkCreateForm.css'

interface VariantsBulkCreateFormProps {
  workId: number
  /** Ratios des master images de l'œuvre (pour filtrage compat format). */
  workSourceRatios: number[]
  /** Tolérance ±5 % miroir `media_service.FORMAT_MATCH_TOLERANCE`. */
  formatMatchingTolerance: number
  onClose: () => void
  /** Callback optionnel post-création — l'invalidation TanStack invalide
   * déjà `worksKeys.detail(workId)`, donc `<VariantsPanel>` rafraîchit seul. */
  onCreated?: (rows: WorkVariantWithCatalogResponse[]) => void
  /** Si défini, affiche « ← Changer de type » à l'étape 1 pour revenir
   *  au picker mono vs bulk. */
  onBack?: () => void
}

/**
 * Refonte digital 2026-05-14 : le mode `digital` est conservé dans le type
 * pour préserver l'invariant Pydantic mais sa carte est retirée de
 * `EDITION_MODE_CARDS` côté UI. Un set numérique = 1 variante par œuvre
 * (BE résout dynamiquement les N fichiers livrés) — bulk n'a aucun sens
 * en digital. L'utilisatrice crée la variante digitale via <VariantModal>
 * mono (picker step 0 de <VariantsPanel>).
 */
type EditionMode = 'original' | 'limited' | 'open' | 'digital'

type WizardStep = 1 | 2 | 3 | 4 | 5

interface PriceRow {
  price_cents: number
  stock_qty: number | null
}

const EDITION_MODE_CARDS: {
  value: EditionMode
  label: string
  description: string
  icon: typeof Circle
}[] = [
  { value: 'original', label: 'Original', description: 'Pièce unique', icon: Disc },
  {
    value: 'limited',
    label: 'Édition limitée',
    description: 'Tirages numérotés',
    icon: CircleDot,
  },
  { value: 'open', label: 'Open edition', description: 'Sans limite', icon: Circle },
  // Refonte digital 2026-05-14 — carte 'digital' retirée du wizard bulk.
  // Pour créer un set numérique, passer par <VariantModal> mono (1 variante
  // par œuvre, BE résout dynamiquement les N fichiers livrés).
]

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Édition',
  2: 'Formats',
  3: 'Composition',
  4: 'Prix & stock',
  5: 'Récap',
}

export default function VariantsBulkCreateForm({
  workId,
  workSourceRatios,
  formatMatchingTolerance,
  onClose,
  onCreated,
  onBack,
}: VariantsBulkCreateFormProps) {
  const formatsQuery = useFormats({ only_active: true })
  const framingsQuery = useFramings({ only_active: true })
  const supportsQuery = useSupports({ only_active: true })
  const bulkAddMutation = useBulkAddVariants()

  const formats = useMemo(() => formatsQuery.data ?? [], [formatsQuery.data])
  const framings = useMemo(() => framingsQuery.data ?? [], [framingsQuery.data])
  const supports = useMemo(() => supportsQuery.data ?? [], [supportsQuery.data])

  // ── Wizard state ─────────────────────────────────────────────────────

  const [step, setStep] = useState<WizardStep>(1)
  const [editionMode, setEditionMode] = useState<EditionMode>('open')
  const [editionSize, setEditionSize] = useState<number | null>(null)
  const [selectedFormatIds, setSelectedFormatIds] = useState<Set<number>>(new Set())
  const [supportId, setSupportId] = useState<number | null>(null)
  const [framingId, setFramingId] = useState<number | null>(null)
  const [pricesByFormat, setPricesByFormat] = useState<Map<number, PriceRow>>(new Map())

  const isDigital = editionMode === 'digital'

  // ── Formats filtrés (ratios + medium_kind) ───────────────────────────

  const compatibleFormats = useMemo(() => {
    return formats.filter((f) => {
      // Ratio compat
      if (workSourceRatios.length > 0) {
        const ratio = Number(f.width_cm) / Number(f.height_cm)
        if (!Number.isFinite(ratio)) return false
        const ok = workSourceRatios.some((r) => {
          const delta = Math.min(Math.abs(r - ratio) / ratio, Math.abs(1 / r - ratio) / ratio)
          return delta <= formatMatchingTolerance
        })
        if (!ok) return false
      }
      // Medium_kind compat
      return isDigital ? f.kinds.includes('digital') : f.kinds.some((k) => k !== 'digital')
    })
  }, [formats, workSourceRatios, formatMatchingTolerance, isDigital])

  // ── Compat / step transitions ─────────────────────────────────────────

  const toggleFormat = (id: number) => {
    setSelectedFormatIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleEditionModeChange = (next: EditionMode) => {
    setEditionMode(next)
    // Reset sélection format si bascule digital/physique (incompatibilité kind).
    setSelectedFormatIds(new Set())
    // Reset compo physique si digital.
    if (next === 'digital') {
      setSupportId(null)
      setFramingId(null)
    }
    // Reset taille édition si pas limited.
    if (next !== 'limited') setEditionSize(null)
  }

  // Tableaux pour les étapes 4/5 — l'ordre matche la sélection insertion.
  const orderedSelectedFormats = useMemo(() => {
    return Array.from(selectedFormatIds)
      .map((id) => formats.find((f) => f.id === id))
      .filter((f): f is (typeof formats)[number] => f !== undefined)
  }, [selectedFormatIds, formats])

  const updatePrice = (formatId: number, patch: Partial<PriceRow>) => {
    setPricesByFormat((prev) => {
      const next = new Map(prev)
      const current = next.get(formatId) ?? { price_cents: 0, stock_qty: null }
      next.set(formatId, { ...current, ...patch })
      return next
    })
  }

  // ── Submit ───────────────────────────────────────────────────────────

  const buildPayload = (): { variants: WorkVariantInput[] } => {
    const medium_kind: WorkVariantInput['medium_kind'] = isDigital
      ? 'digital'
      : editionMode === 'original'
        ? 'original'
        : 'print'
    const edition_kind =
      editionMode === 'original'
        ? ('original' as const)
        : editionMode === 'limited'
          ? ('limited' as const)
          : ('open' as const)

    const variants = orderedSelectedFormats.map((f) => {
      const price = pricesByFormat.get(f.id) ?? { price_cents: 0, stock_qty: null }
      const base: WorkVariantInput = {
        medium_kind,
        format_id: f.id,
        edition_kind,
        edition_size: editionMode === 'limited' ? editionSize : null,
        price_cents: price.price_cents,
        stock_qty: price.stock_qty,
        fragile: !isDigital,
        status: 'draft',
      }
      if (!isDigital) {
        base.support_id = supportId
        base.framing_id = framingId
        // Option B 2026-05-18 — `printable_spec_id` non envoyé : pour POD
        // open le BE auto-fill via les défauts atelier ; pour limited/
        // original le BE rejette explicitement un binding spec (politique
        // V3). Dans les deux cas, l'omission est la valeur correcte.
      }
      return base
    })

    return { variants }
  }

  // Refonte tarifs 2026-05-15 (option B) — POD open print : prix dérivé au
  // publish (`pricing_service.compute_pod_retail_price_cents`), pas saisi.
  // Toute autre combinaison (digital, original, print+limited) exige un
  // `price_cents > 0` net atelier.
  const isPodOpen = !isDigital && editionMode === 'open'

  const handleSubmit = () => {
    const payload = buildPayload()
    if (payload.variants.length === 0) {
      toast.error('Aucun format sélectionné')
      setStep(2)
      return
    }
    if (!isPodOpen) {
      const missingPrice = payload.variants.find((v) => v.price_cents <= 0)
      if (missingPrice) {
        toast.error('Saisis un prix > 0 pour chaque variante')
        setStep(4)
        return
      }
    }
    bulkAddMutation.mutate(
      { workId, payload },
      {
        onSuccess: (rows) => {
          toast.success(rows.length === 1 ? '1 variante créée' : `${rows.length} variantes créées`)
          onCreated?.(rows)
          onClose()
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Création impossible — vérifie les valeurs')
        },
      },
    )
  }

  // ── Step validators (pour disable le bouton "Continuer") ─────────────

  const canContinue: Record<WizardStep, boolean> = {
    1: editionMode !== 'limited' || (editionSize !== null && editionSize >= 1),
    2: selectedFormatIds.size > 0,
    3: true, // composition optionnelle — print/original peut être bindé plus tard
    4: isPodOpen
      ? true
      : orderedSelectedFormats.every((f) => (pricesByFormat.get(f.id)?.price_cents ?? 0) > 0),
    5: true,
  }

  const isLoading = formatsQuery.isLoading || framingsQuery.isLoading || supportsQuery.isLoading

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Décliner en plusieurs variantes"
      size="large"
      kicker="Œuvre · création bulk"
    >
      <WizardStepper currentStep={step} isDigital={isDigital} />

      {step === 1 && (
        <Step1Edition
          editionMode={editionMode}
          onChange={handleEditionModeChange}
          editionSize={editionSize}
          onEditionSizeChange={setEditionSize}
        />
      )}

      {step === 2 && (
        <Step2Formats
          formats={compatibleFormats}
          selected={selectedFormatIds}
          onToggle={toggleFormat}
          isLoading={isLoading}
          isDigital={isDigital}
          hasSourceRatios={workSourceRatios.length > 0}
          totalFormatsCount={formats.length}
        />
      )}

      {step === 3 && !isDigital && (
        <Step3Composition
          supports={supports}
          framings={framings}
          supportId={supportId}
          framingId={framingId}
          isPodOpen={isPodOpen}
          onSupportChange={setSupportId}
          onFramingChange={setFramingId}
        />
      )}

      {step === 3 && isDigital && (
        <Modal.Body>
          <p className="ui-body ui-text-muted">
            Set numérique — pas de composition physique (support, encadrement, spec POD non
            applicables). Continue vers la saisie des prix.
          </p>
        </Modal.Body>
      )}

      {step === 4 && (
        <Step4Prices
          rows={orderedSelectedFormats}
          prices={pricesByFormat}
          onUpdate={updatePrice}
          isPodOpen={isPodOpen}
        />
      )}

      {step === 5 && (
        <Step5Recap
          editionMode={editionMode}
          editionSize={editionSize}
          rows={orderedSelectedFormats}
          prices={pricesByFormat}
          supportLabel={supports.find((s) => s.id === supportId)?.label_fr ?? null}
          framingLabel={framings.find((f) => f.id === framingId)?.label_fr ?? null}
          isDigital={isDigital}
          isPodOpen={isPodOpen}
        />
      )}

      <Modal.Footer
        leftActions={
          step > 1 ? (
            <Button
              variant="ghost"
              type="button"
              onClick={() => setStep((s) => (s > 1 ? ((s - 1) as WizardStep) : s))}
            >
              ← Retour
            </Button>
          ) : onBack ? (
            <Button variant="quiet" type="button" onClick={onBack}>
              ← Changer de type
            </Button>
          ) : undefined
        }
      >
        <Button variant="ghost" type="button" onClick={onClose}>
          Annuler
        </Button>
        {step < 5 && (
          <Button
            variant="primary"
            type="button"
            onClick={() =>
              setStep((s) => {
                // Skip étape 3 si digital (composition non applicable).
                if (s === 2 && isDigital) return 4
                return (s + 1) as WizardStep
              })
            }
            disabled={!canContinue[step]}
          >
            Continuer →
          </Button>
        )}
        {step === 5 && (
          <Button
            variant="primary"
            type="button"
            onClick={handleSubmit}
            loading={bulkAddMutation.isPending}
            disabled={orderedSelectedFormats.length === 0}
          >
            Créer{' '}
            {orderedSelectedFormats.length === 1
              ? '1 variante'
              : `${orderedSelectedFormats.length} variantes`}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Stepper visuel — 5 étapes (l'étape 3 est skippée pour digital)
// ──────────────────────────────────────────────────────────────────────────────

interface WizardStepperProps {
  currentStep: WizardStep
  isDigital: boolean
}

function WizardStepper({ currentStep, isDigital }: WizardStepperProps) {
  const visibleSteps: WizardStep[] = isDigital ? [1, 2, 4, 5] : [1, 2, 3, 4, 5]

  return (
    <ol className="variants-bulk-stepper" aria-label="Progression">
      {visibleSteps.map((step, idx) => (
        <li
          key={step}
          className="variants-bulk-stepper-item"
          data-active={currentStep === step || undefined}
          data-done={currentStep > step || undefined}
        >
          <span className="variants-bulk-stepper-index">{idx + 1}</span>
          <span className="variants-bulk-stepper-label">{STEP_LABELS[step]}</span>
        </li>
      ))}
    </ol>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Étape 1 — Type d'édition
// ──────────────────────────────────────────────────────────────────────────────

interface Step1Props {
  editionMode: EditionMode
  onChange: (next: EditionMode) => void
  editionSize: number | null
  onEditionSizeChange: (next: number | null) => void
}

function Step1Edition({ editionMode, onChange, editionSize, onEditionSizeChange }: Step1Props) {
  return (
    <Modal.Body>
      <p className="ui-body ui-text-muted variants-bulk-intro">
        Le type d'édition s'applique à toutes les variantes du batch. Tu pourras créer un second
        batch (ex. open + limitée) si tu veux mixer.
      </p>
      <p className="ui-body-sm ui-text-muted variants-bulk-intro">
        <em>
          Pour un set numérique, utilise plutôt « Nouvelle variante » mono — 1 variante par œuvre
          suffit (le BE génère automatiquement tous les fichiers compatibles dans le ZIP).
        </em>
      </p>
      <div className="variants-bulk-edition-grid">
        {EDITION_MODE_CARDS.map((card) => {
          const Icon = card.icon
          return (
            <RadioCard
              key={card.value}
              selected={editionMode === card.value}
              onSelect={() => onChange(card.value)}
              label={card.label}
              description={card.description}
              icon={<Icon aria-hidden="true" size={18} />}
            />
          )
        })}
      </div>
      {editionMode === 'limited' && (
        <Field
          label="Taille de l'édition"
          required
          helper="Nombre total de tirages numérotés appliqué à toutes les variantes du batch."
        >
          {(controlProps) => (
            <Input
              {...controlProps}
              type="number"
              min={1}
              value={editionSize ?? ''}
              onChange={(e) => {
                const raw = e.currentTarget.value
                const n = raw === '' ? null : Number(raw)
                onEditionSizeChange(Number.isFinite(n) && n !== null && n >= 1 ? n : null)
              }}
            />
          )}
        </Field>
      )}
    </Modal.Body>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Étape 2 — Formats à décliner
// ──────────────────────────────────────────────────────────────────────────────

interface Step2Props {
  formats: FormatResponse[]
  selected: Set<number>
  onToggle: (id: number) => void
  isLoading: boolean
  isDigital: boolean
  hasSourceRatios: boolean
  totalFormatsCount: number
}

function Step2Formats({
  formats,
  selected,
  onToggle,
  isLoading,
  isDigital,
  hasSourceRatios,
  totalFormatsCount,
}: Step2Props) {
  return (
    <Modal.Body>
      <p className="ui-body ui-text-muted variants-bulk-intro">
        Coche chaque format que tu veux décliner. Chaque format coché créera une variante distincte
        avec la même édition et composition.
        {hasSourceRatios && (
          <>
            {' '}
            Les formats incompatibles avec les ratios de l'image source sont filtrés
            automatiquement.
          </>
        )}
      </p>

      {isLoading && <p className="ui-body ui-text-muted">Chargement…</p>}

      {!isLoading && formats.length === 0 && (
        <p className="ui-body">
          {totalFormatsCount === 0
            ? 'Aucun format dans ton catalogue. Va dans Réglages → Formats pour en créer.'
            : isDigital
              ? 'Aucun format digital actif. Va dans Réglages → Formats pour activer le kind "digital".'
              : 'Aucun format compatible avec les ratios de cette œuvre.'}
        </p>
      )}

      {!isLoading && formats.length > 0 && (
        <div className="variants-bulk-format-grid">
          {formats.map((f) => (
            <RadioCard
              key={f.id}
              multi
              selected={selected.has(f.id)}
              onSelect={() => onToggle(f.id)}
              label={f.label_fr}
              description={`${Number(f.width_cm)} × ${Number(f.height_cm)} cm`}
            />
          ))}
        </div>
      )}

      {selected.size > 0 && (
        <p className="ui-body-sm variants-bulk-selected-count" aria-live="polite">
          <strong>{selected.size}</strong> format{selected.size > 1 ? 's' : ''} sélectionné
          {selected.size > 1 ? 's' : ''}
        </p>
      )}
    </Modal.Body>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Étape 3 — Composition par défaut (support / framing / spec POD)
// ──────────────────────────────────────────────────────────────────────────────

interface Step3Props {
  supports: SupportResponse[]
  framings: FramingResponse[]
  supportId: number | null
  framingId: number | null
  isPodOpen: boolean
  onSupportChange: (id: number | null) => void
  onFramingChange: (id: number | null) => void
}

function Step3Composition({
  supports,
  framings,
  supportId,
  framingId,
  isPodOpen,
  onSupportChange,
  onFramingChange,
}: Step3Props) {
  return (
    <Modal.Body>
      <p className="ui-body ui-text-muted variants-bulk-intro">
        Ces valeurs sont appliquées à toutes les variantes du batch. Tu pourras les modifier
        individuellement plus tard via le bouton « Modifier » sur chaque ligne.
      </p>

      <Field
        label="Support"
        helper="Papier / impression. Optionnel — laisse vide si tu veux fixer par variante."
      >
        {(controlProps) => (
          <Select
            {...controlProps}
            value={supportId === null ? '' : String(supportId)}
            onChange={(e) => {
              const raw = e.currentTarget.value
              onSupportChange(raw === '' ? null : Number(raw))
            }}
          >
            <option value="">— aucun —</option>
            {supports.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.label_fr}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label="Encadrement"
        helper="Cadre / sans cadre. Optionnel — laisse vide si tu veux fixer par variante."
      >
        {(controlProps) => (
          <Select
            {...controlProps}
            value={framingId === null ? '' : String(framingId)}
            onChange={(e) => {
              const raw = e.currentTarget.value
              onFramingChange(raw === '' ? null : Number(raw))
            }}
          >
            <option value="">— aucun —</option>
            {framings.map((f) => (
              <option key={f.id} value={String(f.id)}>
                {f.label_fr}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {isPodOpen && (
        <div className="variants-bulk-pod-matrix-note ui-body-sm ui-text-muted">
          Production POD gérée par la matrice bi-axe (Size × Color), configurée dans{' '}
          <Link to="/settings?tab=printable-specs">Réglages → Catalogue POD</Link>. La matrice
          s'active automatiquement à la création de chaque variante POD.
        </div>
      )}
    </Modal.Body>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Étape 4 — Prix & stock par ligne
// ──────────────────────────────────────────────────────────────────────────────

interface Step4Props {
  rows: { id: number; label_fr: string; width_cm: number | string; height_cm: number | string }[]
  prices: Map<number, PriceRow>
  onUpdate: (formatId: number, patch: Partial<PriceRow>) => void
  isPodOpen: boolean
}

function Step4Prices({ rows, prices, onUpdate, isPodOpen }: Step4Props) {
  return (
    <Modal.Body>
      <p className="ui-body ui-text-muted variants-bulk-intro">
        {isPodOpen
          ? 'Variantes POD — le prix final affiché sur Etsy sera calculé au publish à partir du coût Gelato + marge atelier + frais Etsy (cf. Réglages → Tarification). Tu peux ajuster le stock par défaut (vide = à la demande).'
          : 'Saisis le prix de vente (en euros) et le stock pour chaque variante. Stock vide = à la demande (POD ou digital).'}
      </p>
      <div className="variants-bulk-prices-table-wrapper">
        {!isPodOpen && (
          <p className="ui-body ui-text-muted variants-bulk-prices-helper">
            Saisis le <strong>prix net atelier</strong> que tu veux toucher après prélèvement Etsy
            (frais ~10 %). La compensation est appliquée automatiquement au publish — Stéphanie
            touche pile ce qu'elle a tapé. Voir <em>Réglages → Tarification</em> pour ajuster la
            marge POD et les frais Etsy.
          </p>
        )}
        <table className="variants-bulk-prices-table">
          <thead>
            <tr>
              <th>Format</th>
              {!isPodOpen && <th>Prix net atelier (€)</th>}
              <th>Stock</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => {
              const row = prices.get(f.id) ?? { price_cents: 0, stock_qty: null }
              const priceEuros = row.price_cents / 100
              return (
                <tr key={f.id}>
                  <td>
                    <strong>{f.label_fr}</strong>
                    <span className="variants-bulk-prices-dim ui-body-sm ui-text-muted">
                      {Number(f.width_cm)} × {Number(f.height_cm)} cm
                    </span>
                  </td>
                  {!isPodOpen && (
                    <td>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={priceEuros === 0 ? '' : priceEuros.toFixed(2)}
                        onChange={(e) => {
                          const raw = e.currentTarget.value
                          const n = raw === '' ? 0 : Number(raw)
                          onUpdate(f.id, {
                            price_cents: Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0,
                          })
                        }}
                        aria-label={`Prix pour ${f.label_fr} en euros`}
                      />
                    </td>
                  )}
                  <td>
                    <Input
                      type="number"
                      min={0}
                      step="1"
                      value={row.stock_qty ?? ''}
                      onChange={(e) => {
                        const raw = e.currentTarget.value
                        const n = raw === '' ? null : Number(raw)
                        onUpdate(f.id, {
                          stock_qty: n !== null && Number.isFinite(n) && n >= 0 ? n : null,
                        })
                      }}
                      placeholder="À la demande"
                      aria-label={`Stock pour ${f.label_fr}`}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Modal.Body>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Étape 5 — Récap final
// ──────────────────────────────────────────────────────────────────────────────

interface Step5Props {
  editionMode: EditionMode
  editionSize: number | null
  rows: { id: number; label_fr: string }[]
  prices: Map<number, PriceRow>
  supportLabel: string | null
  framingLabel: string | null
  isDigital: boolean
  isPodOpen: boolean
}

function Step5Recap({
  editionMode,
  editionSize,
  rows,
  prices,
  supportLabel,
  framingLabel,
  isDigital,
  isPodOpen,
}: Step5Props) {
  const editionLabel = isDigital
    ? 'Set numérique'
    : `${EDITION_KIND_FR[editionMode === 'digital' ? 'open' : editionMode]}${
        editionMode === 'limited' && editionSize ? ` /${editionSize}` : ''
      }`

  return (
    <Modal.Body>
      <dl className="variants-bulk-recap">
        <div className="variants-bulk-recap-row">
          <dt className="ui-eyebrow">Édition</dt>
          <dd>
            <Badge variant="default">{editionLabel}</Badge>
          </dd>
        </div>
        {!isDigital && (
          <div className="variants-bulk-recap-row">
            <dt className="ui-eyebrow">Composition</dt>
            <dd>
              <div className="variants-bulk-recap-chips">
                {supportLabel && <Badge size="sm">Support : {supportLabel}</Badge>}
                {framingLabel && <Badge size="sm">Encadrement : {framingLabel}</Badge>}
                {isPodOpen && <Badge size="sm">Matrice POD (auto)</Badge>}
                {!supportLabel && !framingLabel && !isPodOpen && (
                  <span className="ui-text-muted ui-body-sm">— aucune —</span>
                )}
              </div>
            </dd>
          </div>
        )}
        <div className="variants-bulk-recap-row">
          <dt className="ui-eyebrow">Variantes à créer</dt>
          <dd>
            <strong>{rows.length}</strong>
          </dd>
        </div>
      </dl>

      <table className="variants-bulk-recap-table">
        <thead>
          <tr>
            <th>Format</th>
            <th>Prix</th>
            <th>Stock</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => {
            const row = prices.get(f.id) ?? { price_cents: 0, stock_qty: null }
            return (
              <tr key={f.id}>
                <td>{f.label_fr}</td>
                <td className="variants-bulk-recap-numeric">
                  {isPodOpen ? (
                    <span className="ui-text-muted">Calculé au publish</span>
                  ) : (
                    `${(row.price_cents / 100).toFixed(2)} €`
                  )}
                </td>
                <td className="variants-bulk-recap-numeric">{row.stock_qty ?? 'À la demande'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <p className="ui-body-sm ui-text-muted variants-bulk-recap-note">
        Création en transaction tout-ou-rien : si une ligne est invalide côté serveur, toutes les
        variantes sont rollback.
      </p>
    </Modal.Body>
  )
}
